import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import { ArrowRight, Leaf, Mountain, Sun, ShieldCheck } from "lucide-react";

const HERO_IMAGE = "https://images.unsplash.com/photo-1524863479829-916d8e77f114?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNTl8MHwxfHNlYXJjaHwyfHxpbmRpYW4lMjB3ZWxsbmVzcyUyMHlvZ2ElMjBuYXR1cmV8ZW58MHx8fHwxNzgxNDE4NTIyfDA&ixlib=rb-4.1.0&q=85";
const PHIL_IMAGE = "https://images.unsplash.com/photo-1581600140682-d4e68c8cde32?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzF8MHwxfHNlYXJjaHwyfHxheXVydmVkaWMlMjBoZXJicyUyMHNwaWNlcyUyMG5hdHVyYWx8ZW58MHx8fHwxNzgxNDE4NTIyfDA&ixlib=rb-4.1.0&q=85";
const ABOUT_IMAGE = "https://images.unsplash.com/photo-1768700439764-9ec0ba31ae75?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODd8MHwxfHNlYXJjaHwxfHxtb3J0YXIlMjBhbmQlMjBwZXN0bGUlMjBoZXJicyUyMGF5dXJ2ZWRhfGVufDB8fHx8MTc4MTQxODUzMnww&ixlib=rb-4.1.0&q=85";

const PILLARS = [
  { icon: Mountain, title: "Sourced at the Source", text: "Hand-picked herbs from the foothills of the Himalayas and forests of South India." },
  { icon: Leaf, title: "Pure & Unadulterated", text: "No fillers, no chemicals — just classical Ayurvedic formulations the way they were intended." },
  { icon: Sun, title: "Slow-Crafted", text: "Sun-dried, stone-ground, slow-infused. Patience is our most powerful ingredient." },
  { icon: ShieldCheck, title: "Lab-Tested Purity", text: "Every batch is tested for purity, potency and heavy metals before it reaches you." },
];

const TESTIMONIALS = [
  { name: "Priya S.", city: "Mumbai", text: "The Kumkumadi oil has transformed my skin in 3 weeks. My morning ritual is incomplete without it now.", rating: 5 },
  { name: "Arjun M.", city: "Bengaluru", text: "Tried Shilajit from many brands. Oshveda's quality is genuinely different — pure, sticky, the real thing.", rating: 5 },
  { name: "Kavita R.", city: "Delhi", text: "Their Bhringraj oil reduced my hair fall in a month. Authentic Ayurveda, finally.", rating: 5 },
];

export default function Home() {
  const [featured, setFeatured] = useState([]);

  useEffect(() => {
    api.get("/products?featured=true").then((r) => setFeatured(r.data.slice(0, 4))).catch(() => {});
  }, []);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative h-[92vh] min-h-[640px] w-full overflow-hidden" data-testid="hero-section">
        <img src={HERO_IMAGE} alt="Ayurvedic wellness" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-black/40" />
        <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-transparent to-black/60" />
        <div className="relative h-full container-x flex flex-col justify-end pb-20 lg:pb-32 text-oshveda-bg">
          <div className="max-w-3xl animate-fade-up">
            <div className="label-eyebrow !text-oshveda-bg/80 mb-6" style={{ animationDelay: '0ms' }}>
              Pure Ayurveda · est. India
            </div>
            <h1 className="font-serif text-5xl sm:text-6xl md:text-7xl lg:text-8xl leading-[0.95] tracking-tight mb-8" style={{ textShadow: '0 2px 16px rgba(0,0,0,0.35)' }}>
              The wisdom of <em className="text-oshveda-accent not-italic font-serif">ancient herbs.</em>
              <br />
              Crafted for today.
            </h1>
            <p className="text-base md:text-lg max-w-xl text-oshveda-bg/90 leading-relaxed mb-10">
              Oshveda Naturals brings you authentic, lab-tested Ayurvedic remedies — sourced at the source,
              slow-crafted, and trusted by thousands across India.
            </p>
            <div className="flex flex-wrap gap-4">
              <Link to="/shop" data-testid="hero-shop-cta" className="inline-flex items-center gap-2 px-8 py-4 rounded-full bg-oshveda-bg text-oshveda-primary text-sm tracking-widest uppercase font-semibold hover:bg-oshveda-accent hover:text-white hover:-translate-y-0.5 transition-all duration-300">
                Explore the collection <ArrowRight size={16} />
              </Link>
              <Link to="/about" data-testid="hero-story-cta" className="inline-flex items-center gap-2 px-8 py-4 rounded-full border border-oshveda-bg/60 text-oshveda-bg text-sm tracking-widest uppercase hover:bg-oshveda-bg/10 transition-all">
                Our Story
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* PILLARS */}
      <section className="section-y bg-oshveda-bg" data-testid="pillars-section">
        <div className="container-x">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-10 mb-14">
            <div className="md:col-span-5">
              <div className="label-eyebrow mb-4">What sets us apart</div>
              <h2 className="font-serif text-4xl md:text-5xl tracking-tight text-oshveda-ink leading-tight">
                Four principles. <br />
                <em className="text-oshveda-accent not-italic">Zero compromise.</em>
              </h2>
            </div>
            <div className="md:col-span-6 md:col-start-7 flex items-end">
              <p className="text-oshveda-ink2 text-lg leading-relaxed">
                We didn't start Oshveda to be another wellness brand. We started it because we couldn't find Ayurvedic products that we ourselves would trust giving to our parents. So we built it.
              </p>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {PILLARS.map((p, idx) => (
              <div key={p.title} data-testid={`pillar-${idx}`} className="p-8 bg-white rounded-2xl border border-oshveda-borderSubtle hover:border-oshveda-primary/30 transition-colors">
                <div className="w-12 h-12 rounded-full bg-oshveda-primary/5 text-oshveda-primary flex items-center justify-center mb-5">
                  <p.icon size={20} strokeWidth={1.5} />
                </div>
                <h3 className="font-serif text-2xl mb-2 text-oshveda-ink">{p.title}</h3>
                <p className="text-sm text-oshveda-ink2 leading-relaxed">{p.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FEATURED PRODUCTS */}
      <section className="section-y bg-oshveda-bgAlt" data-testid="featured-section">
        <div className="container-x">
          <div className="flex items-end justify-between mb-12 flex-wrap gap-4">
            <div>
              <div className="label-eyebrow mb-3">Bestsellers</div>
              <h2 className="font-serif text-4xl md:text-5xl tracking-tight text-oshveda-ink">Loved across India</h2>
            </div>
            <Link to="/shop" data-testid="view-all-products" className="inline-flex items-center gap-2 text-sm tracking-widest uppercase text-oshveda-primary hover:text-oshveda-accent transition-colors">
              View all <ArrowRight size={16} />
            </Link>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6" data-testid="featured-products">
            {featured.map((p) => <ProductCard key={p.id} product={p} />)}
          </div>
        </div>
      </section>

      {/* PHILOSOPHY */}
      <section className="section-y bg-oshveda-bg" data-testid="philosophy-section">
        <div className="container-x grid grid-cols-1 md:grid-cols-12 gap-12 items-center">
          <div className="md:col-span-6 order-2 md:order-1">
            <div className="label-eyebrow mb-4">5,000 years of wisdom</div>
            <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl tracking-tight leading-[1.05] text-oshveda-ink mb-6">
              Ayurveda doesn't <em className="text-oshveda-accent not-italic">treat the symptom.</em>
              <br />It restores the source.
            </h2>
            <p className="text-oshveda-ink2 text-lg leading-relaxed mb-4">
              For millennia, our ancestors looked to the earth for healing — and the earth answered with herbs, roots, oils and resins that worked with the body, not against it.
            </p>
            <p className="text-oshveda-ink2 text-lg leading-relaxed mb-8">
              Every Oshveda product is built on that same belief: nature already knows. We just have to listen carefully.
            </p>
            <Link to="/about" data-testid="philosophy-cta" className="inline-flex items-center gap-2 px-8 py-3.5 rounded-full bg-oshveda-primary text-oshveda-bg text-sm tracking-widest uppercase hover:bg-oshveda-primaryHover hover:-translate-y-0.5 transition-all">
              Read our story <ArrowRight size={16} />
            </Link>
          </div>
          <div className="md:col-span-6 order-1 md:order-2">
            <div className="aspect-[4/5] rounded-3xl overflow-hidden">
              <img src={PHIL_IMAGE} alt="Ayurvedic herbs" className="w-full h-full object-cover" />
            </div>
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="section-y bg-oshveda-bgAlt" data-testid="testimonials-section">
        <div className="container-x">
          <div className="text-center mb-14">
            <div className="label-eyebrow mb-3">From our family</div>
            <h2 className="font-serif text-4xl md:text-5xl tracking-tight text-oshveda-ink">What our customers say</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {TESTIMONIALS.map((t, idx) => (
              <div key={idx} data-testid={`testimonial-${idx}`} className="p-8 bg-white rounded-2xl border border-oshveda-borderSubtle">
                <div className="text-oshveda-accent mb-4 tracking-widest">★★★★★</div>
                <p className="font-serif italic text-xl text-oshveda-ink leading-snug mb-6">"{t.text}"</p>
                <div className="text-sm">
                  <div className="font-semibold text-oshveda-ink">{t.name}</div>
                  <div className="text-oshveda-muted">{t.city}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA STRIP */}
      <section className="relative section-y overflow-hidden" data-testid="final-cta-section">
        <img src={ABOUT_IMAGE} alt="Mortar pestle" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-oshveda-primary/85" />
        <div className="relative container-x text-center text-oshveda-bg">
          <div className="label-eyebrow !text-oshveda-bg/70 mb-4">Begin your ritual</div>
          <h2 className="font-serif text-4xl md:text-6xl tracking-tight max-w-3xl mx-auto leading-tight mb-8">
            Your journey to <em className="text-oshveda-accent not-italic">natural wellness</em> begins with a single drop.
          </h2>
          <Link to="/shop" data-testid="final-cta" className="inline-flex items-center gap-2 px-10 py-4 rounded-full bg-oshveda-bg text-oshveda-primary text-sm tracking-widest uppercase font-semibold hover:bg-oshveda-accent hover:text-white hover:-translate-y-0.5 transition-all">
            Browse all products <ArrowRight size={16} />
          </Link>
        </div>
      </section>
    </div>
  );
}
